import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode,
} from 'react';
import { toast, ToastContainer } from 'react-toastify';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  title?: string;
  position?: 'top-right' | 'bottom-right' | 'top-left';
}

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    title?: string,
    position?: 'top-right' | 'bottom-right' | 'top-left',
  ) => void;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error(
      'O hook useToast precisa ser utilizado dentro de um ToastProvider',
    );
  }
  return context;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'success',
      duration = 3000,
      title?: string,
      position: 'top-right' | 'bottom-right' | 'top-left' = 'top-right',
    ) => {
      const toastContent = (
        <div className="d-flex flex-column align-items-start">
          {title && <h5 className="mb-0 me-2">{title}</h5>}
          <p className="mb-0">{message}</p>
        </div>
      );

      toast(toastContent, {
        type: type,
        position: position,
        autoClose: duration,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    },
    [],
  );

  const hideToast = useCallback((id: string) => {
    toast.dismiss(id);
  }, []);

  const clearAllToasts = useCallback(() => {
    toast.dismiss();
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast, clearAllToasts }}>
      {children}
      <ToastContainer limit={3} />
    </ToastContext.Provider>
  );
};
